import { Size } from '@view-model/models/common/basic';
import { ForeignObjectAutosize2 } from '@view-model/models/common/components/ForeignObjectAutosize';
import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { contentTransformAtom } from '@user/pages/ViewModelPage/contentTransformAtom';
import { type Position } from '@view-model/models/common/types/ui';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { useHandler } from '@framework/hooks';
import { RightClickMenuTeleporter } from '@user/pages/ViewModelPage/teleporters';
import { useViewPosition } from '@view-model/ui/components/View/ViewContext';

type Props = {
  isOpen: boolean;
  mousePosition: Position | null;
  children: React.ReactNode;
};

const TOP_MARGIN = -16;

export const RightClickMenu: React.FC<Props> = (props: Props) => {
  const { isOpen, mousePosition } = props;
  const [popupMenuSize, setPopupMenuSize] = useState<Size>(Size.empty);
  const { setActiveRightClickMenuIds } = useRightClickMenuOpenState();

  const popupMenuPosition = mousePosition ? { x: mousePosition.x, y: mousePosition.y } : { x: 0, y: 0 };
  const rootNode = useRef<HTMLElement>(document.querySelector('#root'));

  const handleMenuClose = useHandler(() => {
    setActiveRightClickMenuIds([]);
  });

  const propsRef = useRef<Props>(props);
  propsRef.current = props;

  useEffect(() => {
    // ポップアップメニューを表示した場合、どこかをクリックしたら閉じるようにする（メニューをクリックした場合も含めて）
    if (rootNode.current) {
      const node = rootNode.current;
      const handleClick = (e: MouseEvent) => {
        // target が data-right-click-menu を持つ要素の場合は閉じない
        // stopPropagation ではうまくいかなかったため明示的に判定する
        const target = e.target as HTMLElement;
        if (propsRef.current.isOpen && !target.closest('[data-right-click-menu]')) {
          handleMenuClose();
        }
      };
      node.addEventListener('click', handleClick);

      return () => {
        node.removeEventListener('click', handleClick);
      };
    }
  }, [handleMenuClose]);

  return (
    <RightClickMenuTeleporter.Source>
      {isOpen && (
        <RightClickMenuPopup
          popupMenuPosition={popupMenuPosition}
          popupMenuSize={popupMenuSize}
          onSizeChange={setPopupMenuSize}
        >
          {props.children}
        </RightClickMenuPopup>
      )}
    </RightClickMenuTeleporter.Source>
  );
};

type RightClickMenuPopupProps = {
  popupMenuPosition: Position;
  popupMenuSize: Size;
  onSizeChange: (size: Size) => void;
  children: React.ReactNode;
};

// パン・ズームの度に再レンダーされないよう、変換の購読はメニューを開いている間だけにする。
// ノードごとにマウントされるため、常時購読すると全ノードが毎フレーム再レンダーされる
const RightClickMenuPopup: React.FC<RightClickMenuPopupProps> = (props: RightClickMenuPopupProps) => {
  const { popupMenuPosition, popupMenuSize, onSizeChange, children } = props;
  const viewPosition = useViewPosition();
  const contentTransform = useAtomValue(contentTransformAtom);
  const scale = (1 / contentTransform.k) * 0.3; // メニューを常に同じ大きさで表示するために補正する
  const matrix = new DOMMatrix()
    .translateSelf(viewPosition.x, viewPosition.y)
    .translateSelf(popupMenuPosition.x, popupMenuPosition.y)
    .scaleSelf(scale, scale, 1, 1, 0)
    .translateSelf(0, TOP_MARGIN);

  return (
    <g>
      <ForeignObjectAutosize2 transform={matrix.toString()} onSizeChange={onSizeChange}>
        <div
          className="flex flex-col rounded-2xl bg-white py-4 text-5xl shadow-lg"
          style={{
            visibility: popupMenuSize.isEmpty() ? 'hidden' : 'visible',
          }}
        >
          {children}
        </div>
      </ForeignObjectAutosize2>
    </g>
  );
};

type RightClickMenuItemProps = {
  title: string;
  onClick(): void;
};

export const RightClickMenuItem: React.FC<RightClickMenuItemProps> = ({ title, onClick }: RightClickMenuItemProps) => {
  return (
    <button className={'flex cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'} onClick={onClick}>
      <div className="inline-block pl-4">{title}</div>
    </button>
  );
};
